import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiArrowRightLine, RiFlashlightLine, RiSoundModuleLine,
  RiFileTextLine, RiGlobalLine, RiQuillPenLine, RiBarChartLine,
} from 'react-icons/ri';
import { C, LEVEL_COLORS } from '../../data/constants';
import { useAuth } from '../../context/AuthContext';
import './Landing.css';
import KitsuMark from '../../components/brand/KitsuMark';

const FEATURES = [
  { icon: RiFlashlightLine,  title: 'Smart SRS Flashcards',  desc: "Review vocabulary exactly when you're about to forget — scientifically proven retention." },
  { icon: RiSoundModuleLine, title: 'AI Grammar Tutor',       desc: 'Instant explanations, example sentences, and personalised feedback at every level.'      },
  { icon: RiFileTextLine,    title: 'JLPT Simulations N5–N1', desc: 'Realistic mock exams with auto-scoring, time limits, and detailed breakdowns.'           },
  { icon: RiGlobalLine,      title: 'Listening & Reading',    desc: 'Authentic Japanese audio and texts for every JLPT level.'                                 },
  { icon: RiQuillPenLine,    title: 'Kanji Stroke Order',     desc: 'Learn to write 2,000+ kanji with animated stroke guides and instant feedback.'           },
  { icon: RiBarChartLine,    title: 'Progress Analytics',     desc: 'Dashboards tracking your streak, XP, retention, and learning velocity.'                  },
];

/* Level pills. Deliberately carry no "available" flag any more.

   They used to be marked SOON from a hardcoded list, which meant the
   landing page would keep saying "soon" about a level that had already
   been filled with content and shipped — the page had no way to know.
   A claim the code cannot verify is worse than no claim, so the pills
   just name the levels and let the app itself show what is there. */
const LEVEL_PILLS = ['N5', 'N4', 'N3', 'N2', 'N1'];

export default function Landing() {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <div style={{ background: C.bg, minHeight: '100vh' }}>

      {/* ── Nav ─────────────────────────────────────────────────── */}
      <nav className="landing-nav" style={{
        background: scrolled ? 'rgba(8,8,14,0.92)' : 'rgba(8,8,14,0.5)',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <KitsuMark size={28} />
          <span className="brand-text" style={{ fontWeight: 900, fontSize: 18, letterSpacing: -0.5 }}>
            Kitsu<span style={{ color: C.accent }}>Speak</span>
          </span>
        </div>
        <div className="nav-buttons" style={{ display: 'flex', gap: 10 }}>
          {isLoggedIn ? (
            <button className="btn-primary" style={{ padding: '8px 18px', fontSize: 14 }} onClick={() => navigate('/dashboard')}>
              Dashboard
            </button>
          ) : (
            <>
              <button className="btn-ghost" style={{ padding: '8px 18px', fontSize: 14 }} onClick={() => navigate('/login')}>
                Sign In
              </button>
              <button className="btn-primary" style={{ padding: '8px 18px', fontSize: 14 }} onClick={() => navigate('/signup')}>
                Get Started Free
              </button>
            </>
          )}
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="hero-section">
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 70% 55% at 50% 0%, rgba(249,115,22,0.08), transparent 70%)', pointerEvents: 'none' }} />

        <div className="anim-fade-up" style={{ maxWidth: 740, position: 'relative' }}>
          <div className="hero-badge" style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: C.accentDim, border: `1px solid ${C.accentBorder}`,
            borderRadius: 100, padding: '5px 14px', marginBottom: 26,
            fontSize: 12, color: C.accent, fontWeight: 700,
          }}>
            <RiFlashlightLine size={13} /> JLPT N5–N1 · vocabulary, kanji, grammar and mock exams
          </div>

          <h1 style={{ fontSize: 'clamp(44px,7vw,80px)', fontWeight: 900, lineHeight: 1.0, letterSpacing: -3, marginBottom: 18 }}>
            Learn Japanese.<br />
            <span className="shimmer-text">Like a Fox.</span>
          </h1>

          <p style={{ fontSize: 18, color: C.muted, lineHeight: 1.8, maxWidth: 520, margin: '0 auto 36px' }}>
            Smart SRS, JLPT prep N5–N1, kanji stroke training, and an AI grammar tutor — all in one beautifully designed platform.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 44 }}>
            {isLoggedIn ? (
              <button className="btn-primary" style={{ padding: '14px 32px', fontSize: 16 }} onClick={() => navigate('/dashboard')}>
                Go to Dashboard <RiArrowRightLine />
              </button>
            ) : (
              <>
                <button className="btn-primary" style={{ padding: '14px 32px', fontSize: 16 }} onClick={() => navigate('/signup')}>
                  Start Learning Free <RiArrowRightLine />
                </button>
                <button className="btn-ghost" style={{ padding: '14px 32px', fontSize: 16 }} onClick={() => navigate('/login')}>
                  Sign In
                </button>
              </>
            )}
          </div>

          {/* ── Level pills ─────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            {LEVEL_PILLS.map(l => {
              const color = LEVEL_COLORS[l];
              return (
                <span key={l} style={{
                  background: `${color}18`,
                  border: `1px solid ${color}40`,
                  color: color,
                  fontWeight: 800,
                  borderRadius: 8,
                  padding: '6px 18px',
                  fontSize: 14,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                  {l}
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Stats ───────────────────────────────────────────────── */}
      <div style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.surface, padding: '38px 24px' }}>
        <div className="stats-grid">
          {[['6,000+','Vocabulary Words'],['2,000+','Kanji Characters'],['N5–N1','All JLPT Levels'],['900+','Grammar Points']].map(([v, l]) => (
            <div key={l}>
              <div style={{ fontSize: 32, fontWeight: 900, color: C.accent, letterSpacing: -1 }}>{v}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4, fontWeight: 600 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features ────────────────────────────────────────────── */}
      <section style={{ padding: '90px 24px', maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{ display: 'inline-block', background: C.accentDim, border: `1px solid ${C.accentBorder}`, borderRadius: 100, padding: '5px 14px', marginBottom: 14, fontSize: 12, color: C.accent, fontWeight: 700 }}>
            Features
          </div>
          <h2 style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 900, letterSpacing: -1.5, marginBottom: 12 }}>
            Everything to master Japanese
          </h2>
          <p style={{ fontSize: 16, color: C.muted, maxWidth: 480, margin: '0 auto' }}>
            The smartest learning methods in one platform.
          </p>
        </div>
        <div className="features-grid">
          {FEATURES.map(f => (
            <div key={f.title} className="ks-card" style={{ padding: '26px 22px' }}>
              <div style={{ width: 40, height: 40, borderRadius: 9, background: C.accentDim, color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, marginBottom: 14 }}>
                <f.icon />
              </div>
              <h3 style={{ fontWeight: 800, fontSize: 15, marginBottom: 7 }}>{f.title}</h3>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.75 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────── */}
      <section style={{ padding: '70px 40px', textAlign: 'center', borderTop: `1px solid ${C.border}`, background: C.surface }}>
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
          <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'center' }}>
            <KitsuMark size={56} animation="breathe" />
          </div>
          <h2 style={{ fontSize: 'clamp(26px,4vw,42px)', fontWeight: 900, letterSpacing: -1.5, marginBottom: 14 }}>
            Ready to outsmart Japanese?
          </h2>
          <p style={{ fontSize: 16, color: C.muted, marginBottom: 30, lineHeight: 1.75 }}>
            Join thousands of learners who chose the clever path to fluency.
          </p>
          <button className="btn-primary" style={{ padding: '14px 34px', fontSize: 16 }} onClick={() => navigate(isLoggedIn ? '/dashboard' : '/signup')}>
            {isLoggedIn ? 'Continue Learning' : 'Start for Free'} <RiArrowRightLine />
          </button>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="landing-footer" style={{ borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <KitsuMark size={22} />
          <span style={{ fontWeight: 900, fontSize: 15 }}>Kitsu<span style={{ color: C.accent }}>Speak</span></span>
        </div>
        <div style={{ fontSize: 12, color: C.faint }}>© 2026 KitsuSpeak. All rights reserved.</div>
        <div className="footer-links" style={{ display: 'flex', gap: 18 }}>
          {['Instagram', 'TikTok', 'Discord', 'YouTube'].map(s => (
            <span key={s} style={{ fontSize: 12, color: C.muted }}>{s}</span>
          ))}
        </div>
      </footer>
    </div>
  );
}
